/**
 * The "Switch role" group in the user menu: one row per role the account
 * holds, the other roles' unread counts, and "Add a role" when the account
 * may add one.
 */

import type { RoleStatus, UserType } from "@shared/types";
import { Check, Loader2, Plus, RotateCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useRoleActions } from "@/features/roles/hooks/use-role-actions";
import { useUserRoles } from "@/features/roles/hooks/use-user-roles";
import { useNotificationsStore } from "@/features/settings/store/notifications.store";
import { cn } from "@/lib/utils";

const STATUS_LABEL_KEY: Partial<Record<RoleStatus, string>> = {
	pending: "roleSwitcher.status.pending",
	rejected: "roleSwitcher.status.rejected",
};

/** At least 44px tall in the mobile sidebar sheet. */
const rowClass =
	"flex min-h-11 cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-dashboard-text focus:bg-dashboard-divider md:min-h-9 data-[disabled]:cursor-default data-[disabled]:opacity-60";

/** The unread count shown on a row: hidden at 0, "99+" above 99. */
export function formatUnread(count: number): string | null {
	if (count <= 0) return null;
	return count > 99 ? "99+" : String(count);
}

function UnreadBadge({
	count,
	role,
}: {
	count: number;
	role: UserType;
}): React.JSX.Element | null {
	const { t } = useTranslation();
	const shown = formatUnread(count);
	if (!shown) return null;
	return (
		<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-dashboard-accent px-1.5 text-[11px] font-semibold text-dashboard-on-fill">
			<span aria-hidden="true">{shown}</span>
			<span className="sr-only">
				{t("roleSwitcher.unreadAs", {
					count,
					role: t(`roleSession.roleNames.${role}`),
				})}
			</span>
		</span>
	);
}

export function RoleSwitcher({
	onAddRole,
	onSwitched,
}: {
	/** "Add a role" was chosen. */
	onAddRole: () => void;
	/** A switch was made. */
	onSwitched: () => void;
}): React.JSX.Element | null {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useUserRoles();
	const { pendingRole, switchTo } = useRoleActions();
	const otherRoleUnread = useNotificationsStore(
		(state) => state.otherRoleUnread,
	);

	const roles = data?.roles ?? [];
	const canAdd = (data?.addableRoles.length ?? 0) > 0;
	// One role and nothing to add: there is nothing to switch to.
	if (data && roles.length <= 1 && !canAdd) return null;

	const select = (role: UserType, isActive: boolean): void => {
		if (isActive || pendingRole) return;
		void switchTo(role).then((switched) => {
			if (switched) onSwitched();
		});
	};

	return (
		<>
			<DropdownMenuGroup aria-label={t("roleSwitcher.title")}>
				<DropdownMenuLabel className="px-2 pb-1 pt-1.5 text-[11px] font-medium text-dashboard-muted">
					{t("roleSwitcher.title")}
				</DropdownMenuLabel>

				{isLoading && (
					<div role="status" className="space-y-1 px-2 py-1">
						<span className="sr-only">{t("roleSwitcher.loading")}</span>
						<Skeleton className="h-8 w-full bg-dashboard-divider" />
						<Skeleton className="h-8 w-full bg-dashboard-divider" />
					</div>
				)}

				{isError && (
					<DropdownMenuItem
						className={rowClass}
						onSelect={(event) => {
							event.preventDefault();
							void refetch();
						}}
					>
						<span className="flex-1 text-dashboard-muted">
							{t("roleSwitcher.loadFailed")}
						</span>
						<RotateCw className="h-3.5 w-3.5 text-dashboard-muted" />
						{t("roleSwitcher.retry")}
					</DropdownMenuItem>
				)}

				{roles.map((held) => {
					const statusKey = STATUS_LABEL_KEY[held.status];
					return (
						<DropdownMenuItem
							key={held.role}
							role="menuitemradio"
							aria-checked={held.isActive}
							disabled={pendingRole !== null}
							className={rowClass}
							onSelect={(event) => {
								event.preventDefault();
								select(held.role, held.isActive);
							}}
						>
							<span className="flex min-w-0 flex-1 flex-col">
								<span
									className={cn("truncate", held.isActive && "font-semibold")}
								>
									{t(`roleSession.roleNames.${held.role}`)}
								</span>
								{statusKey && (
									<span className="truncate text-[11px] text-dashboard-muted">
										{t(statusKey)}
									</span>
								)}
							</span>
							{!held.isActive && (
								<UnreadBadge
									count={otherRoleUnread[held.role] ?? 0}
									role={held.role}
								/>
							)}
							{pendingRole === held.role ? (
								<Loader2
									aria-hidden="true"
									className="h-3.5 w-3.5 animate-spin text-dashboard-muted"
								/>
							) : held.isActive ? (
								<Check
									aria-hidden="true"
									className="h-3.5 w-3.5 text-dashboard-accent"
								/>
							) : null}
						</DropdownMenuItem>
					);
				})}

				{canAdd && (
					<DropdownMenuItem
						className={rowClass}
						disabled={pendingRole !== null}
						onSelect={onAddRole}
					>
						<Plus className="h-3.5 w-3.5 text-dashboard-muted" />
						{t("roleSwitcher.addRole")}
					</DropdownMenuItem>
				)}
			</DropdownMenuGroup>
			<DropdownMenuSeparator className="bg-dashboard-border" />
		</>
	);
}
